import React, { useCallback } from 'react';
import { Column, HeaderFilter, Search } from "devextreme-react/tree-list";
import { customRenderColumn } from "../../common/Columns";

/**
 * Column Configuration and Helper Functions
 * Handles column visibility, permissions, and rendering logic
 */

export const useColumnHelpers = (currentPermissions) => {
  const isColVisible = useCallback((code) => {
    if (!currentPermissions || !Array.isArray(currentPermissions) || currentPermissions.length === 0) {
      return false;
    }
    try {
      let column = currentPermissions.filter((x) => x.ColumnCode === code)[0];
      return !!column;
    } catch (error) {
      console.error('Error checking column visibility:', error);
      return false;
    }
  }, [currentPermissions]);

  const isColEditable = useCallback((code) => {
    if (!currentPermissions || !Array.isArray(currentPermissions) || currentPermissions.length === 0) {
      return false;
    }
    try {
      let column = currentPermissions.filter((x) => x.ColumnCode === code)[0];
      if (!column) {
        return false;
      }
      return column.Permission === 1;
    } catch (error) {
      console.error('Error checking column editability:', error);
      return false;
    }
  }, [currentPermissions]);

  const isAllowedColumn = useCallback((columnCode) => {
    const AllowedColumns = ["col_id", "col_program", "col_category"];
    return AllowedColumns.includes(columnCode);
  }, []);

  const renderColumn = useCallback((columnKey, component) => {
    // Safety check: if permissions are not loaded yet, don't render any columns
    if (!currentPermissions || !Array.isArray(currentPermissions)) {
      return null;
    }
    
    try {
      const hasPermission = isColVisible(columnKey) || isAllowedColumn(columnKey);
      
      if (hasPermission) {
        // Authorized columns render normally
        const extraProps = { cssClass: "tooltip-target-" + columnKey };
        return customRenderColumn(columnKey, component, extraProps);
      } else {
        // Unauthorized columns are hidden and excluded from column chooser
        const hiddenProps = {
          cssClass: "tooltip-target-" + columnKey,
          visible: false,
          allowHiding: false
        };
        return customRenderColumn(columnKey, component, hiddenProps);
      }
    } catch (error) {
      console.error('Error rendering column:', columnKey, error);
      return null;
    }
  }, [isColVisible, isAllowedColumn, currentPermissions]);

  const getColumnCode = useCallback((col) => {
    if (!col || typeof col !== 'object') {
      return null;
    }
    
    try {
      if (isColVisible(col.localColumnKey) || isAllowedColumn(col.localColumnKey)) {
        return { ...col, allowEditing: col.allowEditing ?? isColEditable(col.localColumnKey) };
      }
      return null;
    } catch (error) {
      console.error('Error getting column code:', error);
      return null;
    }
  }, [isColVisible, isAllowedColumn, isColEditable]);

  const renderColumnCode = useCallback((col, colSet) => {
    if (!colSet || !colSet[col]) {
      return null;
    }
    
    try {
      const columnData = colSet[col];
      return (
        <Column 
          key={columnData.localColumnKey} 
          dataField={columnData.dataField} 
          caption={columnData.caption} 
          allowEditing={columnData.allowEditing} 
          format={columnData.format} 
          dataType={columnData.dataType ?? undefined} 
          alignment={columnData.alignment ?? undefined} 
          width={columnData.width} 
          cellRender={columnData.cellRender ?? undefined} 
          headerCellRender={columnData.headerCellRender ?? undefined} 
          calculateCellValue={columnData.calculateCellValue ?? undefined} 
          setCellValue={columnData.setCellValue ?? undefined} 
          cssClass={columnData.cssClass ?? undefined} 
          visible={columnData.visible ?? true} 
        >
          <HeaderFilter><Search enabled={true} /></HeaderFilter>
        </Column>
      );
    } catch (error) {
      console.error('Error rendering column code:', col, error);
      return null;
    }
  }, []);

  return {
    isColVisible,
    isColEditable,
    isAllowedColumn,
    renderColumn,
    getColumnCode,
    renderColumnCode
  };
};